import { useState } from "react";
import {
  Monitor,
  Smartphone,
  Globe,
  Copy,
  Plus,
  Trash2,
  ShieldCheck,
} from "lucide-react";
import { Button, Badge } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { SettingsSection } from "./SettingsSection";
import { Toggle } from "./Toggle";
import { sessions, apiKeys } from "./data";
import { toast } from "sonner";
import type { Session, ApiKey } from "./types";

export function SecuritySection() {
  return (
    <>
      <TwoFactorSection />
      <div className="border-t border-border my-8" />
      <SessionsSection sessions={sessions} />
      <div className="border-t border-border my-8" />
      <ApiKeysSection apiKeys={apiKeys} />
    </>
  );
}

function TwoFactorSection() {
  const { t } = useTranslation(["settings"]);
  const [enabled, setEnabled] = useState(false);

  return (
    <SettingsSection
      title={t("settings:security.title")}
      description={t("settings:security.description")}
    >
      <div className="rounded-card border border-border p-5 bg-card flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-soft">
            <ShieldCheck size={20} className="text-accent" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-primary">
              {t("settings:security.twoFactor.title")}
            </p>
            <p className="text-[12px] text-secondary mt-0.5">
              {enabled
                ? t("settings:security.twoFactor.enabled")
                : t("settings:security.twoFactor.disabled")}
            </p>
          </div>
        </div>
        <Toggle checked={enabled} onChange={() => setEnabled(!enabled)} />
      </div>
    </SettingsSection>
  );
}

function SessionsSection({ sessions: sessionList }: { sessions: Session[] }) {
  const { t } = useTranslation(["settings"]);
  return (
    <SettingsSection
      title={t("settings:security.sessions.title")}
      description={t("settings:security.sessions.description")}
    >
      <div className="space-y-3">
        {sessionList.map((session) => (
          <div
            key={session.id}
            className="rounded-card border border-border p-4 bg-card flex items-center justify-between"
          >
            <div className="flex items-center gap-3.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-page">
                <DeviceIcon device={session.device} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[13px] font-medium text-primary">
                    {session.device}
                  </p>
                  {session.current && (
                    <Badge
                      color={{
                        bg: "rgba(34,197,94,0.12)",
                        text: "#16a34a",
                      }}
                    >
                      {t("settings:security.sessions.thisDevice")}
                    </Badge>
                  )}
                </div>
                <p className="text-[12px] text-secondary mt-0.5">
                  {session.browser} &middot;{" "}
                  <span className="inline-flex items-center gap-1">
                    <Globe size={11} className="inline" />
                    {session.location}
                  </span>{" "}
                  &middot; {session.lastActive}
                </p>
              </div>
            </div>
            {!session.current && (
              <Button variant="ghost" size="sm" icon={Trash2}>
                {t("settings:security.sessions.revoke")}
              </Button>
            )}
          </div>
        ))}
      </div>
    </SettingsSection>
  );
}

function DeviceIcon({ device }: { device: string }) {
  const isPhone =
    device.toLowerCase().includes("phone") ||
    device.toLowerCase().includes("iphone") ||
    device.toLowerCase().includes("android");

  return isPhone ? (
    <Smartphone size={17} className="text-secondary" />
  ) : (
    <Monitor size={17} className="text-secondary" />
  );
}

function ApiKeysSection({ apiKeys: keyList }: { apiKeys: ApiKey[] }) {
  const { t } = useTranslation(["settings"]);
  return (
    <SettingsSection
      title={t("settings:security.apiKeys.title")}
      description={t("settings:security.apiKeys.description")}
    >
      <div className="flex justify-end mb-4">
        <Button variant="primary" icon={Plus} size="sm">
          {t("settings:security.apiKeys.create")}
        </Button>
      </div>

      <div className="border border-border rounded-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-page">
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("settings:security.apiKeys.columns.name")}
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("settings:security.apiKeys.columns.key")}
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("settings:security.apiKeys.columns.created")}
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("settings:security.apiKeys.columns.lastUsed")}
              </th>
              <th className="text-right text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5 w-[100px]" />
            </tr>
          </thead>
          <tbody>
            {keyList.map((key) => (
              <tr
                key={key.id}
                className="border-b border-border last:border-b-0 hover:bg-card-hover/30 transition-colors"
              >
                <td className="px-4 py-3 text-[13px] font-medium text-primary">
                  {key.name}
                </td>
                <td className="px-4 py-3">
                  <code className="text-[12px] text-secondary font-mono bg-page px-2 py-0.5 rounded">
                    {key.prefix}
                  </code>
                </td>
                <td className="px-4 py-3 text-[13px] text-secondary">
                  {key.createdAt}
                </td>
                <td className="px-4 py-3 text-[13px] text-secondary">
                  {key.lastUsed}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        toast.success(
                          t("settings:security.apiKeys.copied") as string,
                        )
                      }
                      className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SettingsSection>
  );
}
